package gr.tech.pdfgenerator.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaFloppyDisk
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.components.generation.MSAHeader
import gr.tech.pdfgenerator.components.generation.PDFGenerationUI
import gr.tech.pdfgenerator.components.layouts.AuthPageLayout
import gr.tech.pdfgenerator.components.layouts.PDFLayout
import gr.tech.pdfgenerator.infrastructure.api.savePDFWithProjectID
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.models.MSADeliverableDTO
import gr.tech.pdfgenerator.models.user.USER_ROLES
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.util.CONSTANTS
import gr.tech.pdfgenerator.infrastructure.pdf.GeneratePDFTool
import gr.tech.pdfgenerator.infrastructure.pdf.PDFGenStatus
import gr.tech.pdfgenerator.util.RES
import gr.tech.pdfgenerator.util.BACKEND
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

@Page("msa")
@Composable
fun msaGeneratePage() {
    AuthPageLayout(
        title = "Inputs",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        msaGenerateScreen()
    }
}

@Composable
fun msaGenerateScreen() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    val generator = GeneratePDFTool()
    var generationStatus: PDFGenStatus by remember { mutableStateOf(PDFGenStatus.GENERATING) }

    val projectTitleFormatted = GeneratorData.generalInputsState.value.projectName.replace(" ", "")
    val documentTitle = "${projectTitleFormatted}-${getCurrentDateString()}"

    LaunchedEffect(Unit) {
        if (GeneratorData.project.value.id != null) {
            generator.genPDF(
                onGenerationStatus = { status ->
                    console.log("genPDF Status: $status")
                    generationStatus = status
                }
            )
        } else {
            ctx.router.tryRoutingTo(BACKEND.ROUTES.PROJECTS)
        }
    }

    /*
        -------------- PDF UI --------------

        Everything below here is the UI and the generated PDF page.
        Please no fancy calculations or logic down here, try to keep it UI only.

        TODO THIS PAGE IS NOT UP TO DATE WITH NEW RULSET

        Some rules I tried to follow:
        - TODO Margin, Padding, one-off styles were allowed in-line.
        - TODO For styles that require more than one property, turn it into a style.
        - TODO Keep Columns, Rows, and function headers on a one line, so that collapsing for human readability/debugging is nicer.

        There are a lot of lines below, so trying to keep things to a minimum in terms of length for this section.

        ---------------------------------------
     */

    PDFLayout(title = documentTitle) {

        PDFGenerationUI(
            generator = generator,
            status = generationStatus,
            onGoBack = {
                ctx.router.tryRoutingTo("/inputs?projectid=${GeneratorData.project.value.id}")
            },
            onStatusUpdate = { generationStatus = it },
            onSave = { blob ->
                scope.launch {
                    generationStatus = PDFGenStatus.DOWNLOADING
                    if (GeneratorData.project.value.id != null) {
                        savePDFWithProjectID(
                            projectId = GeneratorData.project.value.id!!,
                            projectName = documentTitle,
                            projectType = CONSTANTS.PROJECT_TYPES.MSA,
                            pdfBlob = blob
                        )
                    }
                    generationStatus = PDFGenStatus.DOWNLOADED
                }
            }
        )

        // Page to Print
        Div(ElementToPrintStyle.toAttrs {
            id("element-to-print")
        }) {

            // Page 1
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_1, alt = "page 1 of MSA",  backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    introParagraph(
                        GeneratorData.generalInputsState.value.effectiveDate,
                        GeneratorData.generalInputsState.value.companyName,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.stateOverwrite else GeneratorData.generalInputsState.value.companyState,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.addressOverwrite else GeneratorData.generalInputsState.value.companyAddress,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.cityOverwrite else GeneratorData.generalInputsState.value.companyCity,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.zipOverwrite else GeneratorData.generalInputsState.value.companyZip,
                    )
                }
            }

            // Page 2
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_2, alt = "page 2 of MSA",  backgroundStyle.toAttrs())
            }

            // Page 3
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_3, alt = "page 3 of MSA",  backgroundStyle.toAttrs())
            }

            // Page 4
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_4, alt = "page 4 of MSA",  backgroundStyle.toAttrs())
                toCompanyLine()

                toCustomerLine(
                    GeneratorData.generalInputsState.value.companyName,
                    if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.addressOverwrite else GeneratorData.generalInputsState.value.companyAddress,
                    if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.cityOverwrite else GeneratorData.generalInputsState.value.companyCity,
                    if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.stateOverwrite else GeneratorData.generalInputsState.value.companyState,
                    if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.zipOverwrite else GeneratorData.generalInputsState.value.companyZip,
                )
            }

            // Page 5
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_5, alt = "page 5 of MSA",  backgroundStyle.toAttrs())
                insertField(710, 145, 268, GeneratorData.generalInputsState.value.companyRepresentative, Modifier.fontSize(14.px))

                insertField(708, 475, 268, RES.GENERAL.CEO_NAME, Modifier.fontSize(14.px))
                insertField(740, 475, 268, RES.GENERAL.CEO_TITLE, Modifier.fontSize(14.px))
            }

            // Page 6
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_6, alt = "page 6 of MSA",  backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    sowParagraph(
                        GeneratorData.generalInputsState.value.effectiveDate,
                        GeneratorData.generalInputsState.value.companyName,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.stateOverwrite else GeneratorData.generalInputsState.value.companyState,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.addressOverwrite else GeneratorData.generalInputsState.value.companyAddress,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.cityOverwrite else GeneratorData.generalInputsState.value.companyCity,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.zipOverwrite else GeneratorData.generalInputsState.value.companyZip,
                    )
                    insertP(305, 100, 610, GeneratorData.msaInputsState.value.projectDescription, Modifier.fontSize(14.px).lineHeight(1.25))
                    providerParagraph(GeneratorData.generalInputsState.value.companyUtility)

                    Div(overlayTextStyle.toModifier().width(100.percent).toAttrs{ classes("table-container")}) {
                        Table(
                            Modifier.position(Position.Absolute).top(610.px).left(96.px).width(625.px)
                                .border(1.px, LineStyle.Solid, Colors.Black).toAttrs{
                                    style {
                                        property("border-collapse", "collapse")
                                    }
                                }) {
//                            Tr(Modifier.border(1.px, LineStyle.Solid, Colors.Black).toAttrs()) {
//                                Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(18.percent).toAttrs()) {
//                                    P(pdfTableHeaderStyle.toAttrs()) { Text("Phase / Major Activity") }
//                                }
//                                Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(18.percent).toAttrs()) {
//                                    P(pdfTableHeaderStyle.toAttrs()) { Text("Deliverable") }
//                                }
//                                Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(38.percent).toAttrs()) {
//                                    P(pdfTableHeaderStyle.toAttrs()) { Text("Description") }
//                                }
//                                Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(26.percent).toAttrs()) {
//                                    P(pdfTableHeaderStyle.toAttrs()) { Text("Due Date") }
//                                }
//                            }
                            GeneratorData.msaInputsState.value.efficiencyMeasureList.forEachIndexed() { _, efficiencyMeasure ->
                                displayEfficiencyMeasure(efficiencyMeasure)
                            }
                            repeat(8 - GeneratorData.msaInputsState.value.efficiencyMeasureList.size) {
                                displayEfficiencyMeasure("")
                            }
                        }

                    }
//
//                    GeneratorData.msaInputsState.value.efficiencyMeasureList.getOrNull(0)?.let { item ->
//                        insertP(630, 100, 610, item, Modifier.fontSize(14.px).lineHeight(1.25))
//                    }
//
//                    GeneratorData.msaInputsState.value.efficiencyMeasureList.getOrNull(1)?.let { item ->
//                        insertP(660, 100, 610, item, Modifier.fontSize(14.px).lineHeight(1.25))
//                    }
//
//                    GeneratorData.msaInputsState.value.efficiencyMeasureList.getOrNull(2)?.let { item ->
//                        insertP(690, 100, 610, item, Modifier.fontSize(14.px).lineHeight(1.25))
//                    }
                }
            }

            // New Page 7
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_7, alt = "page 7 of MSA",  backgroundStyle.toAttrs())
                Div(overlayTextStyle.toModifier().width(100.percent).toAttrs{ classes("table-container")}) {
                    Table(
                        Modifier.position(Position.Absolute).top(420.px).left(96.px).width(625.px)
                        .border(1.px, LineStyle.Solid, Colors.Black).toAttrs{
                                style {
                                    property("border-collapse", "collapse")
                                }
                            }) {
                        Tr(Modifier.border(1.px, LineStyle.Solid, Colors.Black).toAttrs()) {
                            Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(18.percent).toAttrs()) {
                                P(pdfTableHeaderStyle.toAttrs()) { Text("Phase / Major Activity") }
                            }
                            Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(18.percent).toAttrs()) {
                                P(pdfTableHeaderStyle.toAttrs()) { Text("Deliverable") }
                            }
                            Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(38.percent).toAttrs()) {
                                P(pdfTableHeaderStyle.toAttrs()) { Text("Description") }
                            }
                            Th(pdfTableColumnStyle.toModifier().then(pdfHeaderBackground.toModifier()).width(26.percent).toAttrs()) {
                                P(pdfTableHeaderStyle.toAttrs()) { Text("Due Date") }
                            }
                        }
                        GeneratorData.msaInputsState.value.deliverableList.forEachIndexed() { _, deliverableRow ->
                            displayPDFDeliverables(deliverableRow)
                        }
                        val blankDeliverable = MSADeliverableDTO("", "", "", "")
                        repeat(8 - GeneratorData.msaInputsState.value.deliverableList.size) {
                            displayPDFDeliverables(blankDeliverable)
                        }
                    }

                }
            }

            // New Page 8
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_8, alt = "page 8 of MSA",  backgroundStyle.toAttrs())
                insertField(125, 329, 300, GeneratorData.generalInputsState.value.expirationDate, Modifier.fontSize(14.px))
            }

            // New Page 9
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_9, alt = "page 9 of MSA",  backgroundStyle.toAttrs())
                insertField(402, 145, 265, GeneratorData.generalInputsState.value.companyRepresentative, Modifier.fontSize(14.px))

                insertField(400, 475, 268, RES.GENERAL.CEO_NAME, Modifier.fontSize(14.px))
                insertField(432, 475, 268, RES.GENERAL.CEO_TITLE, Modifier.fontSize(14.px))
            }

            // New Page 10
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_10, alt = "page 10 of MSA",  backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    assignmentParagraph(
                        GeneratorData.generalInputsState.value.effectiveDate,
                        GeneratorData.generalInputsState.value.companyName,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.stateOverwrite else GeneratorData.generalInputsState.value.companyState,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.addressOverwrite else GeneratorData.generalInputsState.value.companyAddress,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.cityOverwrite else GeneratorData.generalInputsState.value.companyCity,
                        if (GeneratorData.msaInputsState.value.addressOverwriteCheck) GeneratorData.msaInputsState.value.zipOverwrite else GeneratorData.generalInputsState.value.companyZip,
                    )
                    recitalParagraph(GeneratorData.generalInputsState.value.effectiveDate)
                    assignmentParagraph(GeneratorData.msaInputsState.value.utilityProgram)

                    insertField(844, 145, 265, GeneratorData.generalInputsState.value.companyRepresentative, Modifier.fontSize(14.px))

                    insertField(842, 475, 268, RES.GENERAL.CEO_NAME, Modifier.fontSize(14.px))
                    insertField(875, 475, 268, RES.GENERAL.CEO_TITLE, Modifier.fontSize(14.px))
                }
            }

            // New Page 11
            Div(pageStyle.toModifier().toAttrs()) {
                MSAHeader()

                Img(RES.MSA.PAGE_11, alt = "page 11 of MSA",  backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    insertField(130, 100, 450, GeneratorData.msaInputsState.value.authorizerName, Modifier.fontSize(14.px))
                    insertField(145, 100, 450, "c/o "+GeneratorData.generalInputsState.value.companyUtility, Modifier.fontSize(14.px))
                    insertP(270, 96, 610, "I, ${GeneratorData.generalInputsState.value.companyRepresentative} on behalf of my company, ${GeneratorData.generalInputsState.value.companyName}, do hereby authorize my agent below:", Modifier.fontSize(16.px).lineHeight(1.25))
                    insertField(595, 200, 450, GeneratorData.generalInputsState.value.companyRepresentative, Modifier.fontSize(16.px))
                    insertField(667, 215, 450, GeneratorData.generalInputsState.value.companyName, Modifier.fontSize(16.px))

                    insertField(362, 250, 268, RES.GENERAL.FULL_ADDRESS, Modifier.fontSize(14.px))
                }
            }
        }
    }
}


@Composable
fun displayEfficiencyMeasure(measure: String) {
    Tr(Modifier.border(1.px, LineStyle.Solid, Colors.Black).height(35.px).toAttrs()) {
        P(pdfTableTextStyle.toModifier().padding(4.px).toAttrs()) { Text(measure) }
    }
}

@Composable
fun displayPDFDeliverables(deliverableRow: MSADeliverableDTO) {
    Tr(Modifier.border(1.px, LineStyle.Solid, Colors.Black).height(35.px).toAttrs()) {
        Td(pdfTableColumnStyle.toModifier().width(18.percent).toAttrs()) {
            P(pdfTableTextStyle.toAttrs()) { Text(deliverableRow.phase) }
        }
        Td(pdfTableColumnStyle.toModifier().width(18.percent).toAttrs()) {
            P(pdfTableTextStyle.toAttrs()) { Text(deliverableRow.deliverable) }
        }
        Td(pdfTableColumnStyle.toModifier().textAlign(TextAlign.Start).width(32.percent).toAttrs()) {
            P(pdfTableTextStyle.toAttrs()) { Text(deliverableRow.description) }
        }
        Td(pdfTableColumnStyle.toModifier().backgroundColor(Colors.LightYellow).textAlign(TextAlign.Start).width(32.percent).toAttrs()) {
            P(pdfTableTextStyle.toAttrs()) { Text(deliverableRow.dueDate) }
        }
    }
}


// Function to place a text field anywhere on the page.
// Parameters, top: dist from top, left: dist from left, width: box width
// value: to display with .toString(), newModifier: Any extra modifications to make on the text styling.
@Composable
fun insertField(top: Int, left: Int, width: Int, value: Any, newModifier: Modifier) {
    Div(insertedFieldStyle.toModifier().width(width.px).top(top.px).left(left.px).toAttrs()) {
        Span(insertedFieldTextStyle.toModifier().then(newModifier).toAttrs()) {
            Text(value.toString())
        }
    }
}

// Function to place a text field anywhere on the page.
// Parameters, top: dist from top, left: dist from left, width: box width
// value: to display with .toString(), newModifier: Any extra modifications to make on the text styling.
@Composable
fun insertP(top: Int, left: Int, width: Int, value: Any, newModifier: Modifier) {
    Div(insertedFieldStyle.toModifier().width(width.px).top(top.px).left(left.px).toAttrs()) {
        P(insertedFieldTextStyle.toModifier().then(newModifier).toAttrs()) {
            Text(value.toString())
        }
    }
}

//Function to generate the date in a String
//Formatted as follows: YEARMONTHDAY
fun getCurrentDateString(): String {
    val date = Date()
    val year = date.getFullYear().toString()
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    val day = date.getDate().toString().padStart(2, '0')
    return "$year$month$day"
}

/* SPECIFIC TEXT PARAGRAPHS */
// PAGE 1 FIRST PARAGRAPH
@Composable
fun introParagraph(effectiveDate: String, accountName: String, companyState: String, companyAddress: String, companyCity: String, companyZip: String) {
    Div(insertedFieldStyle.toModifier().width(623.px).top(115.px).left(96.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).toAttrs()) {
            Text("THIS MASTER SERVICES AGREEMENT (the \"")
            SpanText("Agreement", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\") is entered into as of ${effectiveDate} (the \"")
            SpanText("Effective Date", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"), by and between ")
            SpanText("Green Rebates L.L.C.", Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(", a State of Delaware limited liability company with its principal place of business located at ")
            SpanText(RES.GENERAL.FULL_ADDRESS, Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(" (hereinafter \"")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"); and ${accountName}, a company of the State of ${companyState} with a principal place of business located at ")
            Text("${companyAddress} ${companyCity} ${companyState} ${companyZip} (\"")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\")(collectively hereinafter ")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" and ")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" referred to as the, \"")
            SpanText("Parties", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\").")
        }
    }
}

// PAGE 4 RECEIPT
@Composable
fun toCompanyLine() {
    Div(insertedFieldStyle.toModifier().width(430.px).top(515.px).left(286.px).margin(leftRight = autoLength).display(
        DisplayStyle.Flex).flexDirection(FlexDirection.Column).toAttrs()) {
        SpanText("GREEN REBATES L.L.C.", Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
        SpanText(RES.GENERAL.STREET_ADDRESS, Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
        SpanText(RES.GENERAL.CITY_STATE_ZIP_ADDRESS, Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
    }
}

// PAGE 4 RECEIPT
@Composable
fun toCustomerLine(accountName: String, companyAddress: String, companyCity: String, companyState: String, companyZip: String) {
    Div(insertedFieldStyle.toModifier().width(430.px).top(600.px).left(286.px).margin(leftRight = autoLength).display(
        DisplayStyle.Flex).flexDirection(FlexDirection.Column).toAttrs()) {
        SpanText("${accountName}", Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
        SpanText("${companyAddress}", Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
        SpanText("${companyCity}, ${companyState} ${companyZip}", Modifier.fontFamily("Times New Roman").fontSize(16.px).lineHeight(1.25))
    }
}

// PAGE 6 FIRST PARAGRAPH
@Composable
fun sowParagraph(effectiveDate: String, accountName: String, companyState: String, companyAddress: String, companyCity: String, companyZip: String) {
    Div(insertedFieldStyle.toModifier().width(623.px).top(115.px).left(96.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).toAttrs()) {
            Text("This STATEMENT OF WORK NO. A-1 (\"")
            SpanText("SOW", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\") is entered into as of ${effectiveDate} (the \"")
            SpanText("SOW Effective Date", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"), by and between ")
            SpanText("Green Rebates L.L.C.", Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(", a State of Delaware limited liability company with its principal place of business located at ")
            SpanText(RES.GENERAL.FULL_ADDRESS, Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(" (hereinafter \"")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"); and ${accountName}, a company of the State of ${companyState} with a principal place of business located at ")
            Text("${companyAddress} ${companyCity} ${companyState} ${companyZip} (\"")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\")(collectively hereinafter ")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" and ")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" referred to as the, \"")
            SpanText("Parties", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"), and incorporates by reference and is subject to the terms and conditions of the ")
            SpanText("Master Services Agreement", Modifier.fontStyle(FontStyle.Italic))
            Text(" dated ${effectiveDate} (\"")
            SpanText("Agreement", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\") the ")
            SpanText("Parties.", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
        }
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).margin(top = 10.px).toAttrs()) {
            Text("The ")
            SpanText("Agreement", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" remains in full force and effect, as supplemented or modified by this ")
            SpanText("SOW.", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
        }
    }
}

@Composable
fun providerParagraph(provider: String) {
    Div(insertedFieldStyle.toModifier().width(623.px).top(430.px).left(96.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).toAttrs()) {
            Text("a. ")
            SpanText("Rebate Procurement. Company ", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("will, on the ")
            SpanText("Customer's", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" behalf, contact and communicate with the  ")
            SpanText("Customer's", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" electricity provider, ${provider} (\"")
            SpanText("Service Provider", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"), in order to procure rebates and incentives in relation to the ")
            SpanText("Efficiency Measures", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" listed below. ")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" will prepare and submit all forms and documents of ")
            SpanText("Service Provider", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" and ")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" for any site visits, inspections, or monitoring necessary for the completion of the rebate project. ")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" will work with ")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text(" to ensure the implementation of the efficiency measures required for the approved incentive. ")
        }
    }
}

//  PAGE 9 INTRO PARAGRAPH
@Composable
fun assignmentParagraph(effectiveDate: String, accountName: String, companyState: String, companyAddress: String, companyCity: String, companyZip: String) {
    Div(insertedFieldStyle.toModifier().width(623.px).top(120.px).left(96.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).toAttrs()) {
            Text("THIS ASSIGNMENT AND ASSUMPTION AGREEMENT (the \"")
            SpanText("Assignment", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\") is entered into as of this ${effectiveDate} (the \"")
            SpanText("Assignment Effective Date", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"), by and between ")
            SpanText("Green Rebates L.L.C.", Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(", a State of Delaware limited liability company with its principal place of business located at ")
            SpanText(RES.GENERAL.FULL_ADDRESS, Modifier.textDecorationLine(TextDecorationLine.Underline))
            Text(" (\"")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\"); and ${accountName}, a company of the State of ${companyState} with a principal place of business located at, ")
            Text("${companyAddress} , ${companyCity} , ${companyState} , ${companyZip} (\"")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bolder).fontStyle(FontStyle.Italic))
            Text("\")")
        }
    }
}

//  PAGE 9 RECITAL PARAGRAPH
@Composable
fun recitalParagraph(effectiveDate: String) {
    Div(insertedFieldStyle.toModifier().width(575.px).top(260.px).left(145.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.25).toAttrs()) {
            Text("The rebates pursuant to the ")
            SpanText("Master Services Agreement", Modifier.fontStyle(FontStyle.Italic))
            Text(", which is incorporated herein, entered into on ${effectiveDate}, by and between, ")
            SpanText("Company", Modifier.fontWeight(FontWeight.Bold).fontStyle(FontStyle.Italic))
            Text(" and ")
            SpanText("Customer", Modifier.fontWeight(FontWeight.Bold).fontStyle(FontStyle.Italic))
            Text(" (the \"")
            SpanText("Agreement", Modifier.fontWeight(FontWeight.Bold).fontStyle(FontStyle.Italic))
            Text("\"), shall herein be referred to as the \"")
            SpanText("Rebate", Modifier.fontWeight(FontWeight.Bold).fontStyle(FontStyle.Italic))
            Text(".\"")
        }
    }
}

//  PAGE 10
@Composable
fun assignmentParagraph(utilityProgram: String) {
    Div(insertedFieldStyle.toModifier().width(625.px).top(488.px).left(96.px).margin(leftRight = autoLength).toAttrs()) {
        P(Modifier.fontFamily("Times New Roman").fontSize(14.px).lineHeight(1.15).toAttrs()) {
            SpanText("      Assignment.   ", Modifier.fontWeight(FontWeight.Bold))
            Text("Both Assignor and Assignee understand and agree that the payee of the incentive payment will be the Assignee. Therefore, Assignor hereby assigns to Assignee, any and all legal or " +
                    "equitable interests, to its $utilityProgram incentive payment check (\"Check\"), and that such Check shall be made payable Assignee. By this assignment, the Assignor renounces and waives any " +
                    "and all rights to the incentive payment, or any element thereof, by Assignee.")
        }
    }
}